import { useDialogContext } from '@ariakit/react'
import { type FC, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	DialogDisclosure,
	DialogDismiss,
	DialogFooter,
	DialogHeader,
	DialogHeading,
	DialogProvider
} from '@/components/ui/dialog-atoms'
import { Flexbox } from '@/components/ui/flexbox'
import { ThumbsDownIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import {
	OpsVettingRejectDialog,
	OpsVettingRejectDialogBody,
	OpsVettingRejectDialogBodyFlagsItem
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useAddServiceGalleryImageFlags } from '@/features/service-vetting/query/use-add-service-gallery-image-flags.ts'
import { useGetServiceFlags } from '@/features/service-vetting/query/use-get-service-flags.ts'
import type {
	ServiceFlag,
	ServiceGalleryImage
} from '@/features/service-vetting/types.ts'
import { useTranslation } from '@/translations/i18n'
import { useRemoveServiceGalleryImageFlags } from '../query/use-remove-service-gallery-image-flags'
import { useUpdateIsApprovedGalleryImg } from '../query/use-update-is-approved-gallery-img'

interface ServiceVettingGalleryImageRejectDialogProps {
	image: ServiceGalleryImage
	serviceId: number
	galleryId: number
	currentFlags: ServiceFlag[]
	disabled?: boolean
}

export const ServiceVettingGalleryImageRejectDialog = ({
	image,
	serviceId,
	galleryId,
	currentFlags,
	disabled
}: ServiceVettingGalleryImageRejectDialogProps) => {
	const { t } = useTranslation()

	return (
		<DialogProvider>
			<DialogDisclosure
				render={
					<ButtonLegacy
						className='w-full'
						disabled={disabled}
						size='sm'
						type='button'
						variant='secondary'
					>
						<ThumbsDownIcon />
						{t('vetting:criteria.commons.reject')}
					</ButtonLegacy>
				}
			/>
			<OpsVettingRejectDialog hideOnInteractOutside={false} width='600px'>
				<ServiceVettingGalleryImageRejectDialogBody
					currentFlags={currentFlags}
					galleryId={galleryId}
					image={image}
					serviceId={serviceId}
				/>
			</OpsVettingRejectDialog>
		</DialogProvider>
	)
}

export const ServiceVettingGalleryImageRejectDialogBody: FC<
	ServiceVettingGalleryImageRejectDialogProps
> = ({ image, galleryId, currentFlags }) => {
	const { t } = useTranslation()
	const dialogStore = useDialogContext()
	if (!dialogStore) {
		throw new Error('Dialog must be wrapped in DialogProvider')
	}

	const [selectedFlags, setSelectedFlags] =
		useState<ServiceFlag[]>(currentFlags)

	const updateIsApprovedGalleryImg = useUpdateIsApprovedGalleryImg({
		serviceId: image.submission_id,
		galleryId,
		imgId: image.id
	})

	const addServiceGalleryImageFlags = useAddServiceGalleryImageFlags({
		serviceId: image.submission_id,
		sectionId: galleryId
	})

	const removeServiceGalleryImageFlags = useRemoveServiceGalleryImageFlags({
		serviceId: image.submission_id,
		sectionId: galleryId
	})

	const { data: flags, isLoading: isFlagsLoading } = useGetServiceFlags({
		types: ['gallery_image'],
		response_type: 'extended'
	})

	const isSubmitting =
		updateIsApprovedGalleryImg.isPending ||
		addServiceGalleryImageFlags.isPending

	const hasSelectedFlags = selectedFlags.length > 0

	const handleAddFlag = (flag: ServiceFlag) => {
		setSelectedFlags((prev) => [...prev, flag])
	}

	const handleRemoveFlag = (flag: ServiceFlag) => {
		if (currentFlags.some((f) => f.id === flag.id)) {
			removeServiceGalleryImageFlags.mutate({
				flags: [flag],
				itemId: image.id
			})
		}
		setSelectedFlags((prev) => prev.filter((f) => f.id !== flag.id))
	}

	const handleReject = async () => {
		try {
			// First, mark the image as rejected
			await updateIsApprovedGalleryImg.mutateAsync({ is_approved: false })

			// If there are flags, add them
			if (selectedFlags.length > 0) {
				await addServiceGalleryImageFlags.mutateAsync({
					flags: selectedFlags,
					itemId: image.id
				})
			}
		} catch (error) {
			console.error('Error rejecting image:', error)
		} finally {
			dialogStore.hide()
		}
	}

	return (
		<>
			<DialogHeader>
				<DialogDismiss disabled={isSubmitting} />
				<DialogHeading>
					{t('vetting:criteria.commons.select_flags')}
				</DialogHeading>
			</DialogHeader>
			<OpsVettingRejectDialogBody>
				<GalleryImageItem src={image.url} />
				<ScrollArea scrollbar={<Scrollbar />}>
					<Flexbox direction='column' gap={2}>
						{isFlagsLoading
							? Array.from(
									{ length: 10 },
									(_, i) => `skeleton-${i}`
								).map((key) => (
									<Flexbox gap={2} key={key}>
										<Skeleton height={20} width={20} />
										<Skeleton height={20} width='100%' />
									</Flexbox>
								))
							: flags?.map((flag) => (
									<OpsVettingRejectDialogBodyFlagsItem
										gap={2}
										key={flag.id}
									>
										<Checkbox
											checked={selectedFlags.some(
												(f) => f.id === flag.id
											)}
											onChange={() => {
												if (
													selectedFlags.some(
														(f) => f.id === flag.id
													)
												) {
													handleRemoveFlag(flag)
												} else {
													handleAddFlag(flag)
												}
											}}
										/>
										<p>
											{t(
												`vetting:criteria.service.flags_key_name.${flag.key_name}`
											)}
										</p>
									</OpsVettingRejectDialogBodyFlagsItem>
								))}
					</Flexbox>
				</ScrollArea>
			</OpsVettingRejectDialogBody>
			<DialogFooter>
				<ButtonLegacy
					disabled={isSubmitting}
					onClick={() => dialogStore.hide()}
					type='button'
					variant='tertiary'
				>
					{t('common:cancel')}
				</ButtonLegacy>

				<ButtonLegacy
					disabled={isSubmitting || !hasSelectedFlags}
					onClick={handleReject}
					type='button'
				>
					{t('vetting:criteria.commons.reject_image')}
					{isSubmitting && <Spinner size={20} />}
				</ButtonLegacy>
			</DialogFooter>
		</>
	)
}
